import { useState, useCallback, useRef } from "react";
import { audioupload } from "../components/funtionsBD/Mainservice";
import { loadCoachSessionSettings } from "../utils/liveTrainingSettings";

export function useCoachMediaCapture({ socket, teId, tsId, coachUserId, onFeedbackSent }) {
  const [activeRecording, setActiveRecording] = useState(null); // { type, playerId, userId, tableNumber }
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const lastVoiceUrlRef = useRef(null);

  const startVideoRecording = useCallback(
    async ({ playerId, userId, tableNumber }) => {
      if (!socket || !teId || !coachUserId || !playerId || !userId) return;
      try {
        const { getClipConsentStatus } = await import("../api/socialClipApi");
        const { CONSENT_PURPOSES } = await import("../constants/legalPolicy");
        const consent = await getClipConsentStatus({
          teId,
          purpose: CONSENT_PURPOSES.TRAINING_ANALYSIS,
        });
        const subjects = consent?.subjects || [];
        const self = subjects.find(
          (s) => String(s.user_id) === String(userId)
        );
        // Only this player's grant matters — a teammate without consent must
        // not block recording someone who is cleared.
        if (self && !self.granted) {
          window.alert(
            "This player has not granted coach video consent. A parent can grant it in the Parent Hub. A paper form is only a fallback."
          );
          return;
        }
      } catch {
        /* fail closed only when the API positively denies; network errors still allow coach UX, server will refuse upload */
      }
      setActiveRecording({ type: "video", playerId, userId, tableNumber });

      const fps = loadCoachSessionSettings(tsId).recommendedRecordingFps;
      socket.emit("start_table_video_recording", {
        te_id: teId,
        ts_id: tsId,
        coach_id: coachUserId,
        player_id: playerId,
        receiver_user_id: userId,
        table_number: tableNumber,
        fps,
      });
    },
    [socket, teId, tsId, coachUserId]
  );

  const stopVideoRecording = useCallback(() => {
    if (!socket || !teId || !coachUserId || !activeRecording || activeRecording.type !== "video") {
      return;
    }

    socket.emit("stop_table_video_recording", {
      te_id: teId,
      ts_id: tsId,
      coach_id: coachUserId,
      player_id: activeRecording.playerId,
      receiver_user_id: activeRecording.userId,
      voice_message_url: lastVoiceUrlRef.current || null,
      table_number: activeRecording.tableNumber,
    });
    setActiveRecording(null);
  }, [socket, teId, tsId, coachUserId, activeRecording]);

  const startVoiceRecording = useCallback(
    async ({ playerId, userId, tableNumber }) => {
      if (!socket || !teId || !coachUserId || !playerId || !userId) return;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorderRef.current.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          audioChunksRef.current = [];

          try {
            const params = new FormData();
            params.append("voiceMessage", audioBlob);
            const response = await audioupload(params);
            const voiceUrl = response.data.data;
            lastVoiceUrlRef.current = voiceUrl;

            socket.emit("sendMessage", {
              te_id: teId,
              ts_id: tsId,
              sender_id: coachUserId,
              receiver_id: userId,
              message_type: "voice",
              text_message: null,
              voice_message_url: voiceUrl,
            });
            onFeedbackSent?.({
              receiverUserId: userId,
              messageType: "voice",
              voiceMessageUrl: voiceUrl,
            });

            socket.emit("stop_coach_voice_recording", {
              te_id: teId,
              ts_id: tsId,
              sender_id: coachUserId,
              receiver_id: userId,
              voice_message_url: voiceUrl,
              table_number: tableNumber,
            });
          } catch (err) {
            console.error("Audio upload failed:", err);
            socket.emit("stop_coach_voice_recording", {
              te_id: teId,
              ts_id: tsId,
              sender_id: coachUserId,
              receiver_id: userId,
              error: true,
              table_number: tableNumber,
            });
          }

          stream.getTracks().forEach((track) => track.stop());
        };

        mediaRecorderRef.current.start();
        setActiveRecording({ type: "voice", playerId, userId, tableNumber });

        socket.emit("start_coach_voice_recording", {
          te_id: teId,
          ts_id: tsId,
          coach_id: coachUserId,
          player_id: playerId,
          receiver_user_id: userId,
          table_number: tableNumber,
        });
      } catch (err) {
        console.error("Failed to start voice recording:", err);
      }
    },
    [socket, teId, tsId, coachUserId, onFeedbackSent]
  );

  const stopVoiceRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setActiveRecording(null);
  }, []);

  const stopCapture = useCallback(() => {
    if (!activeRecording) return;
    if (activeRecording.type === "video") {
      stopVideoRecording();
    } else if (activeRecording.type === "voice") {
      stopVoiceRecording();
    }
  }, [activeRecording, stopVideoRecording, stopVoiceRecording]);

  return {
    activeRecording,
    startVoiceRecording,
    startVideoRecording,
    stopVideoRecording,
    stopVoiceRecording,
    stopCapture,
  };
}
